import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import toast from "react-hot-toast";
import {
  ArrowLeft,
  ChevronRight,
  Package,
  Store,
  CreditCard,
  FileText,
  CheckCircle,
  Download,
  Loader2,
  Pencil,
  Trash2,
  Ban,
  RotateCcw,
  RefreshCw,
  AlertCircle,
  Copy,
  Check,
  Info,
  Mail,
  Phone,
  Printer,
} from "lucide-react";
import PurchaseConfirmDialog from "./PurchaseConfirmDialog";
import {
  API,
  STATUS_STYLE,
  PAYMENT_STYLE,
  fmtMoney,
  fmtDate,
  fmtDateTime,
  purchaseNumber,
  errMsg,
  itemName,
  itemSku,
  itemImage,
  itemTotal,
  isManualItem,
  paidAmount,
  balanceDue,
  getTotals,
  stockSummary,
} from "./Purchaseshared";

const cardCls = "bg-white rounded-xl border border-slate-200 shadow-[0_1px_2px_rgba(16,24,40,0.04)]";

const Card = ({ title, icon: Icon, children }) => (
  <section className={cardCls}>
    <header className="flex items-center gap-2 px-5 py-3.5 bg-slate-50 border-b border-slate-200 rounded-t-xl">
      {Icon && <Icon size={16} className="text-slate-500" />}
      <h2 className="text-[15px] font-bold text-slate-800">{title}</h2>
    </header>
    {children}
  </section>
);

const InfoRow = ({ label, children }) => (
  <div className="flex items-start justify-between gap-4 py-2 text-sm">
    <dt className="text-slate-500 shrink-0">{label}</dt>
    <dd className="text-slate-800 text-right break-words min-w-0">{children}</dd>
  </div>
);

const Thumb = ({ item }) => {
  const [failed, setFailed] = useState(false);
  const src = itemImage(item);
  return (
    <div className="w-16 h-16 shrink-0 rounded-lg border border-slate-200 bg-slate-50 overflow-hidden flex items-center justify-center">
      {src && !failed ? (
        <img src={src} alt={itemName(item)} onError={() => setFailed(true)} className="w-full h-full object-cover" />
      ) : (
        <Package className="text-slate-300" size={24} />
      )}
    </div>
  );
};

const AdminPurchaseDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [purchase, setPurchase] = useState(null);
  const [loading, setLoading] = useState(true);
  const [billing, setBilling] = useState(false);
  const [copied, setCopied] = useState(false);
  const [pending, setPending] = useState(null); // "receive" | "reopen" | "cancel" | "delete"
  const [working, setWorking] = useState(false);

  const fetchPurchase = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/purchases/${id}`);
      setPurchase(data.purchase);
    } catch (err) {
      console.error(err);
      toast.error(errMsg(err, "Failed to load purchase details"));
      setPurchase(null);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchPurchase();
  }, [fetchPurchase]);

  const totals = useMemo(() => (purchase ? getTotals(purchase) : null), [purchase]);
  const stock = useMemo(() => (purchase ? stockSummary(purchase) : null), [purchase]);

  const handleBill = async () => {
    setBilling(true);
    try {
      const { downloadPurchaseBill } = await import("./purchaseBill");
      downloadPurchaseBill(purchase);
    } catch (err) {
      console.error(err);
      toast.error("Couldn't create the bill. Please try again.");
    } finally {
      setBilling(false);
    }
  };

  const copyNumber = async () => {
    try {
      await navigator.clipboard.writeText(purchaseNumber(purchase));
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error("Could not copy");
    }
  };

  const confirmCopy = useMemo(() => {
    if (!pending || !purchase) return null;
    const stockText = `${stock.units} unit${stock.units === 1 ? "" : "s"} across ${stock.products} catalogue product${stock.products === 1 ? "" : "s"}`;
    const manualText = stock.manual ? ` ${stock.manual} manual item${stock.manual > 1 ? "s are" : " is"} not tracked in inventory.` : "";
    const num_ = purchaseNumber(purchase);

    switch (pending) {
      case "receive":
        return {
          title: "Mark as received?",
          message: `Purchase ${num_} has arrived from ${purchase.supplier?.businessName || purchase.supplier?.fullName || purchase.supplierName || "the vendor"}.`,
          details: `${stockText} will be added to inventory.${manualText}`,
          confirmLabel: "Mark as received",
          tone: "primary",
        };
      case "reopen":
        return {
          title: "Reopen this purchase?",
          message: `Purchase ${num_} will go back to Pending.`,
          details: "No stock change. You can mark it as received later.",
          confirmLabel: "Reopen",
          tone: "primary",
        };
      case "cancel":
        return {
          title: "Cancel this purchase?",
          message: `Purchase ${num_} will be marked as Cancelled.`,
          details:
            purchase.status === "Received"
              ? `The ${stockText} added by this purchase will be removed from inventory again. This fails if some of those units were already sold.`
              : "No stock was added, so inventory is not affected.",
          confirmLabel: "Cancel purchase",
          tone: "danger",
        };
      default:
        return {
          title: "Delete this purchase?",
          message: `Purchase ${num_} will be permanently deleted. This can't be undone.`,
          details:
            purchase.status === "Received"
              ? `The ${stockText} it added will be removed from inventory first. This fails if some of those units were already sold.`
              : "No inventory will change.",
          confirmLabel: "Delete purchase",
          tone: "danger",
        };
    }
  }, [pending, purchase, stock]);

  const runAction = async () => {
    setWorking(true);
    try {
      if (pending === "delete") {
        await axios.delete(`${API}/purchases/${id}`);
        toast.success("Purchase deleted");
        navigate("/admin-purchase");
        return;
      }
      const status = { receive: "Received", reopen: "Pending", cancel: "Cancelled" }[pending];
      await axios.put(`${API}/purchases/${id}/status`, { status });
      toast.success(`Purchase marked as ${status.toLowerCase()}`);
      setPending(null);
      await fetchPurchase();
    } catch (err) {
      console.error(err);
      toast.error(errMsg(err, "That didn't work. Please try again."));
      setPending(null);
    } finally {
      setWorking(false);
    }
  };

  /* ---------- states ---------- */
  if (loading && !purchase) {
    return (
      <div className="min-h-screen bg-[#F3F6FB] p-6 animate-pulse">
        <div className="max-w-[1280px] mx-auto space-y-5">
          <div className="h-4 w-48 bg-slate-200 rounded" />
          <div className="h-32 bg-white border border-slate-200 rounded-xl" />
          <div className="grid lg:grid-cols-[1fr_340px] gap-5">
            <div className="h-96 bg-white border border-slate-200 rounded-xl" />
            <div className="h-96 bg-white border border-slate-200 rounded-xl" />
          </div>
        </div>
      </div>
    );
  }

  if (!purchase) {
    return (
      <div className="min-h-screen bg-[#F3F6FB] flex flex-col items-center justify-center p-6 text-center">
        <AlertCircle size={44} className="text-slate-300" />
        <p className="mt-3 text-lg font-semibold text-slate-800">Purchase not found</p>
        <p className="text-sm text-slate-500 mt-1">It may have been deleted, or the server could not be reached.</p>
        <div className="flex gap-3 mt-5">
          <button onClick={fetchPurchase} className="px-4 py-2 rounded-lg text-sm font-medium bg-[#b07d17] text-white hover:bg-[#8c6312]">
            Try again
          </button>
          <button
            onClick={() => navigate("/admin-purchase")}
            className="px-4 py-2 rounded-lg text-sm font-medium bg-white border border-slate-300 hover:bg-slate-50"
          >
            Back to purchases
          </button>
        </div>
      </div>
    );
  }

  const style = STATUS_STYLE[purchase.status] || STATUS_STYLE.Pending;
  const supplier = purchase.supplier || {};
  const supplierName = supplier.businessName || supplier.fullName || purchase.supplierName || "Manual Vendor";
  const items = purchase.products || [];
  const due = balanceDue(purchase);

  const banner = {
    Pending: {
      tone: "bg-amber-50 border-amber-200 text-amber-800",
      text: "Waiting for goods from the vendor. No stock has been added yet. Mark it as received when the goods arrive.",
    },
    Received: {
      tone: "bg-emerald-50 border-emerald-200 text-emerald-800",
      text: `Received on ${fmtDate(purchase.receivedAt || purchase.updatedAt)}. ${stock.units} unit${stock.units === 1 ? "" : "s"} across ${stock.products} catalogue product${stock.products === 1 ? "" : "s"} added to inventory.${stock.manual ? ` ${stock.manual} manual item${stock.manual > 1 ? "s were" : " was"} not tracked.` : ""
        }`,
    },
    Cancelled: {
      tone: "bg-red-50 border-red-200 text-red-800",
      text: "This purchase was cancelled. It has no effect on inventory.",
    },
  }[purchase.status];

  return (
    <div className="min-h-screen bg-[#F3F6FB] p-4 md:p-6 font-sans">
      <div className="w-full max-w-[1280px] mx-auto space-y-5">
        {/* Breadcrumb */}
        <nav className="flex items-center gap-1.5 text-sm text-slate-500 print:hidden" aria-label="Breadcrumb">
          <button
            onClick={() => navigate("/admin-purchase")}
            className="flex items-center gap-1.5 text-blue-600 hover:text-blue-700 hover:underline"
          >
            <ArrowLeft size={15} /> Purchases
          </button>
          <ChevronRight size={14} />
          <span className="text-slate-700 font-medium">{purchaseNumber(purchase)}</span>
        </nav>

        {/* Header card */}
        <section className={`${cardCls} overflow-hidden`}>
          <div className={`h-1 ${style.bar}`} />
          <div className="p-5 flex flex-col lg:flex-row lg:items-start justify-between gap-5">
            <div className="min-w-0">
              <h1 className="flex flex-wrap items-center gap-x-3 gap-y-2 text-[26px] leading-8 font-extrabold text-white tracking-tight">
                <FileText className="text-[#c59a3f]" size={26} />
                {purchaseNumber(purchase)}
                <button
                  onClick={copyNumber}
                  aria-label="Copy purchase number"
                  className="p-1 rounded hover:bg-slate-100 text-slate-500 print:hidden"
                >
                  {copied ? <Check size={16} className="text-emerald-600" /> : <Copy size={16} />}
                </button>
                <span className={`text-xs font-semibold px-3 py-1 rounded-full ${style.pill}`}>{purchase.status}</span>
                <span
                  className={`text-xs font-semibold px-3 py-1 rounded-full border ${PAYMENT_STYLE[purchase.paymentStatus] || PAYMENT_STYLE.Unpaid}`}
                >
                  {purchase.paymentStatus || "Unpaid"}
                </span>
              </h1>
              <p className="mt-3 text-sm text-slate-600">
                From <b className="text-slate-800 font-semibold">{supplierName}</b>
                {" · "}placed {fmtDate(purchase.purchaseDate || purchase.createdAt)}
                {" · "}
                {items.length} item{items.length === 1 ? "" : "s"}
                {" · "}
                <b className="text-slate-800 font-semibold">{fmtMoney(totals.total)}</b>
              </p>
            </div>

            <div className="flex flex-wrap gap-2.5 print:hidden">
              <button
                onClick={handleBill}
                disabled={billing}
                className="inline-flex items-center gap-2 px-5 py-2 rounded-lg text-sm font-medium text-white border border-transparent bg-[#b07d17] hover:bg-[#8c6312] shadow-sm disabled:opacity-60 transition-colors"
              >
                {billing ? <Loader2 size={15} className="animate-spin" /> : <Download size={15} />}
                Download bill
              </button>
              {purchase.status === "Pending" && (
                <button
                  onClick={() => setPending("receive")}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-emerald-700 bg-emerald-50 border border-emerald-200 hover:bg-emerald-100 transition-colors"
                >
                  <CheckCircle size={15} /> Mark as received
                </button>
              )}
              {purchase.status === "Received" && (
                <button
                  onClick={() => setPending("reopen")}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-slate-700 bg-white border border-slate-300 hover:bg-slate-50 transition-colors"
                >
                  <RotateCcw size={15} /> Back to pending
                </button>
              )}
              {purchase.status === "Cancelled" && (
                <button
                  onClick={() => setPending("reopen")}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-slate-700 bg-white border border-slate-300 hover:bg-slate-50 transition-colors"
                >
                  <RotateCcw size={15} /> Reopen
                </button>
              )}
              <button
                onClick={() => navigate(`/admin-purchase/edit/${purchase._id}`)}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-slate-700 bg-white border border-slate-300 hover:bg-slate-50 transition-colors"
              >
                <Pencil size={15} /> Edit
              </button>
              <button
                onClick={() => window.print()}
                aria-label="Print page"
                className="p-2 rounded-lg text-slate-600 bg-white border border-slate-300 hover:bg-slate-50 transition-colors"
              >
                <Printer size={16} />
              </button>
              <button
                onClick={fetchPurchase}
                disabled={loading}
                aria-label="Refresh"
                className="p-2 rounded-lg text-slate-600 bg-white border border-slate-300 hover:bg-slate-50 disabled:opacity-60 transition-colors"
              >
                <RefreshCw size={16} className={loading ? "animate-spin" : ""} />
              </button>
            </div>
          </div>

          <div className="px-5 pb-5">
            <div className={`flex gap-2.5 rounded-lg border px-4 py-3 text-sm ${banner.tone}`}>
              <Info size={16} className="mt-0.5 shrink-0" />
              <p>{banner.text}</p>
            </div>
          </div>
        </section>

        <div className="grid grid-cols-1 lg:grid-cols-[1fr_340px] gap-5 items-start">
          {/* LEFT */}
          <div className="space-y-5 min-w-0">
            <Card title={`Purchased items (${items.length})`} icon={Package}>
              <div className="overflow-x-auto">
                <table className="w-full text-sm text-left min-w-[620px]">
                  <thead>
                    <tr className="text-[12px] font-semibold text-slate-600 border-b border-slate-200">
                      <th className="px-5 py-3">Item</th>
                      <th className="px-3 py-3 text-center">Qty</th>
                      <th className="px-3 py-3 text-right">Unit cost</th>
                      <th className="px-5 py-3 text-right">Amount</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {items.map((it, i) => (
                      <tr key={it._id || i} className="align-top">
                        <td className="px-5 py-4">
                          <div className="flex gap-4">
                            <Thumb item={it} />
                            <div className="min-w-0">
                              <p className="text-[15px] font-medium text-blue-700 line-clamp-2">{itemName(it)}</p>
                              <p className="text-[12.5px] text-slate-500 mt-1">SKU: {itemSku(it) || "--"}</p>
                              <span
                                className={`inline-block mt-2 text-[11px] font-medium px-2 py-0.5 rounded-full border ${isManualItem(it)
                                  ? "bg-amber-50 text-amber-700 border-amber-200"
                                  : "bg-slate-50 text-slate-600 border-slate-200"
                                  }`}
                              >
                                {isManualItem(it) ? "Manual item · not in inventory" : "Catalogue item"}
                              </span>
                            </div>
                          </div>
                        </td>
                        <td className="px-3 py-4 text-center font-medium text-slate-800">{it.quantity}</td>
                        <td className="px-3 py-4 text-right text-slate-700 whitespace-nowrap">{fmtMoney(it.unitCost)}</td>
                        <td className="px-5 py-4 text-right font-semibold text-white whitespace-nowrap">{fmtMoney(itemTotal(it))}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="border-t border-slate-200 px-5 py-4 flex justify-end">
                <dl className="w-full sm:w-80 text-sm space-y-1.5">
                  <div className="flex justify-between">
                    <dt className="text-slate-500">Items subtotal</dt>
                    <dd className="text-slate-800">{fmtMoney(totals.subTotal)}</dd>
                  </div>
                  {totals.discount > 0 && (
                    <div className="flex justify-between">
                      <dt className="text-slate-500">Discount</dt>
                      <dd className="text-slate-800">− {fmtMoney(totals.discount)}</dd>
                    </div>
                  )}
                  {(totals.tax > 0 || totals.gstPercent > 0) && (
                    <div className="flex justify-between">
                      <dt className="text-slate-500">GST ({totals.gstPercent}%)</dt>
                      <dd className="text-slate-800">{fmtMoney(totals.tax)}</dd>
                    </div>
                  )}
                  {totals.shipping > 0 && (
                    <div className="flex justify-between">
                      <dt className="text-slate-500">Shipping / other</dt>
                      <dd className="text-slate-800">{fmtMoney(totals.shipping)}</dd>
                    </div>
                  )}
                  <div className="flex justify-between pt-2.5 mt-1 border-t border-slate-200 text-base font-bold text-white">
                    <dt>Grand total</dt>
                    <dd>{fmtMoney(totals.total)}</dd>
                  </div>
                  <div className="flex justify-between">
                    <dt className="text-slate-500">Paid</dt>
                    <dd className="text-slate-800">{fmtMoney(paidAmount(purchase))}</dd>
                  </div>
                  <div className="flex justify-between font-medium">
                    <dt className="text-slate-500">Balance due</dt>
                    <dd className={due > 0 && purchase.status !== "Cancelled" ? "text-red-600" : "text-emerald-600"}>{fmtMoney(due)}</dd>
                  </div>
                </dl>
              </div>
            </Card>

            {purchase.notes && (
              <Card title="Notes" icon={FileText}>
                <p className="px-5 py-4 text-sm text-slate-700 whitespace-pre-wrap">{purchase.notes}</p>
              </Card>
            )}
          </div>

          {/* RIGHT */}
          <aside className="space-y-5">
            <Card title="Purchase information" icon={FileText}>
              <dl className="px-5 py-3 divide-y divide-slate-100">
                <InfoRow label="Purchase no.">
                  <span className="font-mono text-xs">{purchaseNumber(purchase)}</span>
                </InfoRow>
                {purchase.supplierInvoiceNo && <InfoRow label="Vendor invoice">{purchase.supplierInvoiceNo}</InfoRow>}
                <InfoRow label="Purchase date">{fmtDate(purchase.purchaseDate || purchase.createdAt)}</InfoRow>
                <InfoRow label="Created">{fmtDateTime(purchase.createdAt)}</InfoRow>
                {purchase.receivedAt && <InfoRow label="Received">{fmtDateTime(purchase.receivedAt)}</InfoRow>}
                <InfoRow label="Last updated">{fmtDateTime(purchase.updatedAt)}</InfoRow>
              </dl>
            </Card>

            <Card title="Vendor" icon={Store}>
              <div className="px-5 py-4 text-sm text-slate-700 space-y-1.5">
                <p className="font-bold text-white text-base">{supplier.businessName || supplier.fullName || "Unknown vendor"}</p>
                {supplier.businessName && supplier.fullName && <p>{supplier.fullName}</p>}
                {supplier.emailId && (
                  <p className="flex items-center gap-1.5 text-slate-600 break-all">
                    <Mail size={13} /> {supplier.emailId}
                  </p>
                )}
                {supplier.mobNo && (
                  <p className="flex items-center gap-1.5 text-slate-600">
                    <Phone size={13} /> {supplier.mobNo}
                  </p>
                )}
              </div>
            </Card>

            <Card title="Payment" icon={CreditCard}>
              <dl className="px-5 py-3 divide-y divide-slate-100">
                <InfoRow label="Status">
                  <span
                    className={`text-[11px] font-semibold px-2 py-0.5 rounded-full border ${PAYMENT_STYLE[purchase.paymentStatus] || PAYMENT_STYLE.Unpaid}`}
                  >
                    {purchase.paymentStatus || "Unpaid"}
                  </span>
                </InfoRow>
                <InfoRow label="Method">{purchase.paymentMethod || "--"}</InfoRow>
                <InfoRow label="Paid">{fmtMoney(paidAmount(purchase))}</InfoRow>
                <InfoRow label="Balance due">
                  <b className={due > 0 && purchase.status !== "Cancelled" ? "text-red-600" : "text-emerald-600"}>{fmtMoney(due)}</b>
                </InfoRow>
              </dl>
            </Card>

            <Card title="Manage purchase" icon={Pencil}>
              <div className="px-5 py-4 flex items-center gap-5 text-sm print:hidden">
                {purchase.status !== "Cancelled" && (
                  <button onClick={() => setPending("cancel")} className="inline-flex items-center gap-1.5 text-slate-600 hover:text-red-600">
                    <Ban size={14} /> Cancel purchase
                  </button>
                )}
                <button onClick={() => setPending("delete")} className="inline-flex items-center gap-1.5 text-slate-600 hover:text-red-600">
                  <Trash2 size={14} /> Delete
                </button>
              </div>
            </Card>
          </aside>
        </div>
      </div>

      <PurchaseConfirmDialog
        open={Boolean(pending)}
        {...(confirmCopy || {})}
        loading={working}
        onConfirm={runAction}
        onCancel={() => !working && setPending(null)}
      />
    </div>
  );
};

export default AdminPurchaseDetails;
